import { defineStore } from 'pinia'
import { onBeforeUnmount, ref } from 'vue'
import { md5 } from 'js-md5'
import { DateTime } from 'ts-luxon'

type ConnectionType = {
  hash: string
  entity: EventSource
  count: number
  callbacks: { [index: string]: Function }
}

export const useSse = defineStore('sse', () => {
  const connections = ref<ConnectionType[]>([])
  const baseUrl = import.meta.env.VITE_BASE_URL

  /**
   * Функция установки sse соединения
   * @param url
   * @param callback
   */
  const getConnection = (url: string, callback: Function) => {
    const hash = md5(url)
    let connection = connections.value[hash]
    const callbackId = md5(DateTime.now().millisecond.toString())

    if (connection) {
      connection.count++
      connection.callbacks[callbackId] = callback
    } else {
      const entity = new EventSource(baseUrl + url)

      /** Переключаемся при потере соединения */
      entity.onerror = () => {
        entity.close()

        setTimeout(() => getConnection(url, callback), 2000)
      }

      connection = {
        hash,
        entity,
        count: 1,
        callbacks: { [callbackId]: callback },
      }

      connections.value[hash] = connection
    }

    connection.entity.addEventListener('message', (event: any) => callback(JSON.parse(event.data)))

    return { callbackId, connection }
  }

  /**
   * Функция удаления sse соединения
   * @param url
   * @param callbackId
   */
  const closeConnection = (url: string, callbackId: string) => {
    const hash = md5(url)
    let connection = connections.value[hash]

    if (connection) {
      connection.count--

      if (connection.count === 0) {
        connection.entity.close()
        delete connections.value[hash]
      } else {
        connection.entity.removeEventListener('message', connection.callbacks[callbackId])
        delete connection.callbacks[callbackId]
      }
    }
  }

  return {
    getConnection,
    closeConnection,
  }
})

/**
 * Функция установки sse для конкретного соединения
 * @param callback
 * @param unmountCallback
 */
export const useSseConnection = (callback: Function, unmountCallback?: Function) => {
  const { getConnection, closeConnection } = useSse()
  const sseConnection = ref<{ callbackId: string; connection: any }>()
  const urlSseConnection = ref<string>('')

  const handler = (url: string) => {
    if (sseConnection.value) {
      closeConnection(urlSseConnection.value, sseConnection.value.callbackId)
    }

    urlSseConnection.value = url
    sseConnection.value = getConnection(urlSseConnection.value, callback)
  }

  onBeforeUnmount(() => {
    if (sseConnection.value) {
      closeConnection(urlSseConnection.value, sseConnection.value.callbackId)
    }

    if (unmountCallback) unmountCallback()
  })

  return {
    handler,
  }
}
